// src/services/userService.js
//
// User API service
// ================
//
// PURPOSE
// -------
// This module contains thin frontend API helpers for user-related backend calls.
//
// CURRENT APP ARCHITECTURE
// ------------------------
// 1) apiClient is the single owner of:
//    - identity headers
//    - token refresh
//    - session selector propagation
//
// 2) This service should therefore stay thin:
//    - build requests
//    - validate obvious caller mistakes
//    - support AbortSignal where useful
//    - rethrow errors to pages/components
//
// 3) This file must NOT:
//    - perform redirects
//    - perform logout
//    - decide auth recovery
//
// ROUTE GROUPS
// ------------
// A) Default endpoints (ACTIVE users only)
//    - GET /auth/users
//    - GET /auth/users/:userId
//
// B) Admin endpoints (FULL list incl. inactive)
//    - GET /auth/users/admin/all
//    - GET /auth/users/admin/:userId
//
// C) Write endpoints
//    - PUT    /auth/update/:userId
//    - PUT    /auth/reset-password/:userId
//    - DELETE /auth/users/:userId
//    - GET    /user-settings/:userId
//    - PUT    /user-settings/update/:userId
//    - PUT    /user-settings/:userId/commission-plan
//
// IMPORTANT BACKEND POLICY
// ------------------------
// - normal app usage should see ACTIVE users only
// - admin/superuser screens may need the full list
//
// Therefore we intentionally expose BOTH endpoint families.
//
// ERROR HANDLING POLICY
// ---------------------
// - cancellations are normal control flow
// - non-cancellation failures are logged and rethrown
// - UI decides how to display messages

import apiClient from "../utils/apiClient";
import { isRequestCanceled, assertValidId } from "./httpUtils";

/* -------------------------------------------------------------------------- */
/* Route constants                                                            */
/* -------------------------------------------------------------------------- */

const ROUTES = Object.freeze({
  USERS: "/auth/users",
  USERS_ADMIN_ALL: "/auth/users/admin/all",

  // IMPORTANT:
  // Adjust this single constant if your real Express router uses
  // another path shape for authController.resetPassword.
  //
  // Example alternatives you might have in backend:
  //   "/auth/reset-password"
  //   "/auth/users/reset-password"
  //   "/auth/reset"
  //
  // The function below assumes final request shape:
  //   PUT {ROUTES.RESET_PASSWORD}/:userId
  //
  RESET_PASSWORD: "/auth/reset-password",

  USER_SETTINGS: "/user-settings",
});

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Extract a user-friendly error message from backend / axios errors.
 *
 * @param {any} error
 * @param {string} fallback
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback = "Request failed.") {
  const msg =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message;

  const clean = typeof msg === "string" ? msg.trim() : "";
  return clean || fallback;
}

/**
 * Build a standard axios config object for this service.
 *
 * Why centralize this?
 * --------------------
 * Because this app uses one shared apiClient with refresh/session logic,
 * and all thin services should construct requests in the same predictable way.
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {object}
 */
function buildRequestConfig(options = {}) {
  return {
    withCredentials: true,
    signal: options.signal,
  };
}

/* -------------------------------------------------------------------------- */
/* Default endpoints (ACTIVE-only)                                            */
/* -------------------------------------------------------------------------- */

/**
 * Fetch users for normal application usage.
 *
 * Endpoint:
 *   GET /auth/users
 *
 * Backend policy:
 * - returns ACTIVE users only
 *
 * Typical usage:
 * - assignee dropdowns
 * - standard user pickers
 * - non-admin forms
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object[]>}
 */
export async function fetchUsers(options = {}) {
  try {
    const response = await apiClient.get(
      ROUTES.USERS,
      buildRequestConfig(options)
    );

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[UserService] ❌ fetchUsers failed:", error);
    throw error;
  }
}

/**
 * Fetch one active user by id.
 *
 * Endpoint:
 *   GET /auth/users/:userId
 *
 * IMPORTANT:
 * - backend may intentionally return 404 for inactive users
 * - admin screens should use fetchUserByIdAdmin instead
 *
 * @param {number|string} userId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function fetchUserById(userId, options = {}) {
  try {
    assertValidId(userId, "userId");

    const response = await apiClient.get(
      `${ROUTES.USERS}/${userId}`,
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(`[UserService] ❌ fetchUserById(${userId}) failed:`, error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Admin endpoints (FULL list / FULL single)                                  */
/* -------------------------------------------------------------------------- */

/**
 * Fetch all users, including inactive ones.
 *
 * Endpoint:
 *   GET /auth/users/admin/all
 *
 * Backend protection:
 * - authenticated
 * - role-restricted (e.g. superuser)
 *
 * Typical usage:
 * - user management screens
 * - admin audit views
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object[]>}
 */
export async function fetchUsersAdminAll(options = {}) {
  try {
    const response = await apiClient.get(
      ROUTES.USERS_ADMIN_ALL,
      buildRequestConfig(options)
    );

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[UserService] ❌ fetchUsersAdminAll failed:", error);
    throw error;
  }
}

/**
 * Fetch one user by id, including inactive users.
 *
 * Endpoint:
 *   GET /auth/users/admin/:userId
 *
 * @param {number|string} userId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function fetchUserByIdAdmin(userId, options = {}) {
  try {
    assertValidId(userId, "userId");

    const response = await apiClient.get(
      `${ROUTES.USERS}/admin/${userId}`,
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(`[UserService] ❌ fetchUserByIdAdmin(${userId}) failed:`, error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* User updates                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Update one user field.
 *
 * Endpoint:
 *   PUT /auth/update/:userId
 *
 * Payload shape:
 *   { [field]: value }
 *
 * Why single-field style is useful:
 * - simple backend validation
 * - explicit auditability
 * - avoids accidental overwrites
 *
 * @param {number|string} userId
 * @param {string} field
 * @param {any} value
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>}
 */
export async function updateUserField(userId, field, value, options = {}) {
  try {
    assertValidId(userId, "userId");

    if (!field || typeof field !== "string") {
      throw new Error(`Invalid field name: ${field}`);
    }

    const response = await apiClient.put(
      `/auth/update/${userId}`,
      { [field]: value },
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(
      `[UserService] ❌ updateUserField(${userId}, ${field}) failed:`,
      error
    );
    throw error;
  }
}

/**
 * Reset password for a selected user from the admin area.
 *
 * Endpoint:
 *   PUT /auth/reset-password/:userId
 *
 * Backend controller expectation:
 *   req.params.user_id (or equivalent route param)
 *   req.body.password
 *
 * IMPORTANT
 * ---------
 * This function belongs in userService, not in AuthContext.
 *
 * Why?
 * ----
 * Because this is an ADMIN user-management operation against another user,
 * not a "current logged-in user auth session" concern.
 *
 * That architectural distinction is exactly what fixes your runtime bug:
 * UserManagementPage should import this function directly from userService,
 * instead of trying to read it from AuthContext.
 *
 * @param {number|string} userId
 * @param {string} password
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>}
 */
export async function resetUserPassword(userId, password, options = {}) {
  try {
    assertValidId(userId, "userId");

    if (typeof password !== "string" || password.length === 0) {
      throw new Error("Password is required.");
    }

    const response = await apiClient.put(
      `${ROUTES.RESET_PASSWORD}/${userId}`,
      { password },
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(
      `[UserService] ❌ resetUserPassword(${userId}) failed:`,
      error
    );
    throw error;
  }
}

/**
 * Delete one user by id.
 *
 * Endpoint:
 *   DELETE /auth/users/:userId
 *
 * @param {number|string} userId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>}
 */
export async function deleteUserById(userId, options = {}) {
  try {
    assertValidId(userId, "userId");

    const response = await apiClient.delete(
      `${ROUTES.USERS}/${userId}`,
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(`[UserService] ❌ deleteUserById(${userId}) failed:`, error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* User settings                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Fetch user settings by user id.
 *
 * Endpoint:
 *   GET /user-settings/:userId
 *
 * @param {number|string} userId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function fetchUserSettings(userId, options = {}) {
  try {
    assertValidId(userId, "userId");

    const response = await apiClient.get(
      `${ROUTES.USER_SETTINGS}/${userId}`,
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(`[UserService] ❌ fetchUserSettings(${userId}) failed:`, error);
    throw error;
  }
}


/**
 * Fetch the current authenticated user's read-only transparency settings.
 *
 * Endpoint:
 *   GET /user-settings/me/transparency
 *
 * Purpose:
 * - employee self-service view under HR,
 * - returns only the current user's own data,
 * - exposes commission rules for transparency without granting edit rights.
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{user: object, settings: object}>}
 */
export async function fetchMyTransparencySettings(options = {}) {
  try {
    const response = await apiClient.get(
      `${ROUTES.USER_SETTINGS}/me/transparency`,
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[UserService] ❌ fetchMyTransparencySettings failed:", error);
    throw error;
  }
}

/**
 * Update one field in user settings.
 *
 * Endpoint:
 *   PUT /user-settings/update/:userId
 *
 * Payload shape:
 *   { [field]: value }
 *
 * @param {number|string} userId
 * @param {string} field
 * @param {any} value
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>}
 */
export async function updateUserSettingsField(userId, field, value, options = {}) {
  try {
    assertValidId(userId, "userId");

    if (!field || typeof field !== "string") {
      throw new Error(`Invalid settings field name: ${field}`);
    }

    const response = await apiClient.put(
      `/user-settings/update/${userId}`,
      { [field]: value },
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(
      `[UserService] ❌ updateUserSettingsField(${userId}, ${field}) failed:`,
      error
    );
    throw error;
  }
}

/**
 * Update the full commission plan atomically.
 *
 * Endpoint:
 *   PUT /user-settings/:userId/commission-plan
 *
 * Payload example:
 * {
 *   mode: "LINEAR" | "TIERED",
 *   linear?: { commission_service, commission_product },
 *   tiers?:  [{ min_amount, max_amount, service_rate, product_rate }]
 * }
 *
 * @param {number|string} userId
 * @param {object} payload
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>}
 */
export async function updateCommissionPlan(userId, payload, options = {}) {
  try {
    assertValidId(userId, "userId");

    const response = await apiClient.put(
      `${ROUTES.USER_SETTINGS}/${userId}/commission-plan`,
      payload,
      buildRequestConfig(options)
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(
      `[UserService] ❌ updateCommissionPlan(${userId}) failed:`,
      error
    );
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Default export                                                             */
/* -------------------------------------------------------------------------- */

export default {
  fetchUsers,
  fetchUserById,
  fetchUsersAdminAll,
  fetchUserByIdAdmin,
  updateUserField,
  resetUserPassword,
  fetchUserSettings,
  fetchMyTransparencySettings,
  updateUserSettingsField,
  deleteUserById,
  updateCommissionPlan,
  getApiErrorMessage,
};

